import { useState } from 'react'

import * as AlertDialog from '@radix-ui/react-alert-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { AnimatePresence, motion } from 'framer-motion'
import { MoreHorizontal, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import Pencil from '@/assets/note-pencil.svg?react'
import { useApi } from '@/hooks/useApi'
import useIsMobileDevice from '@/hooks/utils/useIsMobileDevice'
import { cn } from '@/lib/utils'
import { useAllConversationsStore } from '@/store/useAllConversationsStore'
import { useItemColors } from '@/utils/useItemColors'

/**
 * ConversationItemDropdown – the dropdown menu for a conversation item.
 *
 * @param {boolean} isActive  Whether the conversation is active
 * @param {string} convId  The ID of the conversation
 * @returns {JSX.Element}  The dropdown menu
 */
export const ConversationItemDropdown = ({ isActive, convId }) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const visible = open || isActive
  const menuItem = useItemColors({ isActive: false })
  const dropDownMenuItem = cn(
    menuItem,
    'focus:ring-0 ',
    'touch:min-h-10  group relative mx-1.5 my-0 flex select-none items-center gap-1.5 rounded-[10px] px-2.5 py-2 pe-8 text-sm focus-visible:outline-0 disabled:pointer-events-none ',
  )
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const setEditingId = useAllConversationsStore(s => s.setEditingTitleId)
  const deleteConversation = useAllConversationsStore(s => s.deleteConversation)
  const api = useApi()
  const handleDeleteConfirm = ev => {
    ev.stopPropagation()
    if (isActive) {
      navigate('/chat', { replace: true })
    }
    deleteConversation(api, convId)
  }
  const isMobileDevice = useIsMobileDevice()

  return (
    <AlertDialog.Root
      forceMount
      open={deleteDialogOpen}
      onOpenChange={setDeleteDialogOpen}
    >
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <div className="isolate hidden items-center self-stretch whitespace-nowrap pe-4 md:flex">
            <div className="absolute overflow-hidden whitespace-nowrap p-0">
              <button
                className={cn(
                  // useItemColors({ isActive: false }),
                  '-me-2 min-h-9 items-center self-stretch rounded-md py-2 pe-[10px] text-foreground hover:opacity-100',
                  visible && 'static',
                  visible ? 'opacity-100' : 'opacity-0',
                  'focus-within:opacity-100 focus:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 group-focus:opacity-100',
                )}
                aria-label="Open conversation options"
                type="button"
              >
                <MoreHorizontal className="icon-md" />
              </button>
            </div>
          </div>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <AnimatePresence>
            {open && (
              <DropdownMenu.Content
                asChild
                forceMount
                side="bottom"
                align="end"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className={cn(
                    'z-50 select-none overflow-y-auto rounded-2xl bg-clip-padding p-1 py-1.5 shadow-lg will-change-[opacity,transform] text-sm',
                    'border-gray-normal border bg-white text-menus focus:ring-0',
                    'backdrop-blur-lg new-chat:bg-black/20 dark:bg-black/20 dark:text-white',
                  )}
                >
                  <DropdownMenu.Item
                    className={cn(dropDownMenuItem, isMobileDevice && 'hidden')}
                    onClick={ev => {
                      ev.stopPropagation()
                      setEditingId(convId)
                    }}
                  >
                    <div className="flex size-[18px] shrink-0 items-center justify-center">
                      <Pencil className="icon-md" />
                    </div>
                    Rename
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator
                    className={cn(
                      'bg-gray-200 dark:bg-gray-700 my-1 h-px',
                      isMobileDevice && 'hidden',
                    )}
                  />
                  <AlertDialog.Trigger asChild>
                    <DropdownMenu.Item
                      onClick={e => {
                        e.stopPropagation()
                        e.preventDefault()
                        setOpen(false)
                        setTimeout(() => {
                          setDeleteDialogOpen(true)
                        }, 0)
                      }}
                      className={cn(
                        dropDownMenuItem,
                        'hover:[#4d100e] hover:bg-red--400/20 dark:hover:bg-red--500/20 hover:text-[#e02e2a] dark:hover:text-[#ff8583]',
                        'text-[#e02e2a] dark:text-[#ff8583]',
                      )}
                    >
                      <div className="flex size-[18px] shrink-0 items-center justify-center">
                        <Trash2 className="icon-md" />
                      </div>
                      Delete
                    </DropdownMenu.Item>
                  </AlertDialog.Trigger>
                </motion.div>
              </DropdownMenu.Content>
            )}
          </AnimatePresence>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 bg-blacka-6" />
        <AlertDialog.Content className="data-[state=open]:animate-contentShow bg-gray-app fixed left-1/2 top-1/2 max-h-[85vh] w-[90vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-md border-white p-[25px] focus:outline-none">
          <AlertDialog.Title className="text-mauve-normal m-0 font-semibold text-base">
            Delete this conversation?
          </AlertDialog.Title>
          <AlertDialog.Description className="text-mauve-dim mb-5 mt-[15px] leading-normal text-[15px]">
            This action can’t be undone. The conversation and all its messages
            will be permanently removed.
          </AlertDialog.Description>

          <div className="flex justify-end gap-[25px]">
            <AlertDialog.Cancel asChild>
              <button
                className={cn(
                  'bg-mauve-ghost text-mauve-normal inline-flex h-[35px] select-none items-center justify-center rounded px-[15px] font-semibold leading-none outline-none outline-offset-1',
                  'focus-visible:outline-2 focus-visible:outline-mauve-7',
                )}
              >
                Cancel
              </button>
            </AlertDialog.Cancel>

            <AlertDialog.Action asChild>
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-action text-red-normal inline-flex h-[35px] select-none items-center justify-center rounded px-[15px] font-semibold leading-none outline-none outline-offset-1 focus-visible:outline-2 focus-visible:outline-red-7"
              >
                Delete
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
